import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { adminService, Category } from '../../services/admin';

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryColor, setCategoryColor] = useState('#3B82F6');
  const [categoryOrder, setCategoryOrder] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await adminService.getCategories();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
              setShowSuccessMessage('ການດຶງຂໍ້ມູນໝວດໝູ່ລົ້ມເຫລວ');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryDescription('');
    setCategoryColor('#3B82F6');
    setCategoryOrder(1);
    setShowModal(true);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryDescription(category.description || '');
    setCategoryColor(category.color);
    setCategoryOrder(category.order);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setCategoryName('');
    setCategoryDescription('');
    setCategoryColor('#3B82F6');
    setCategoryOrder(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryName.trim()) {
      setShowSuccessMessage('Please enter a category name');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      if (editingCategory) {
        await adminService.updateCategory(editingCategory.id, {
          name: categoryName.trim(),
          description: categoryDescription.trim(),
          color: categoryColor,
          order: categoryOrder
        });
        setShowSuccessMessage('Category updated successfully!');
      } else {
        await adminService.createCategory({
          name: categoryName.trim(),
          description: categoryDescription.trim(),
          color: categoryColor,
          order: categoryOrder
        });
        setShowSuccessMessage('Category created successfully!');
      }
      
      closeModal();
      fetchCategories();
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving category:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Unknown error';
        setShowSuccessMessage(`ການບັນທຶກໝວດໝູ່ລົ້ມເຫລວ: ${errorMessage}`);
      } else {
                  setShowSuccessMessage('ການບັນທຶກໝວດໝູ່ລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.');
      }
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => setShowSuccessMessage(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCategory = (category: Category) => {
    setCategoryToDelete(category);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    try {
      await adminService.deleteCategory(categoryToDelete.id);
      setShowSuccessMessage(`Category "${categoryToDelete.name}" deleted successfully!`);
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccessMessage(null), 3000);
      
      fetchCategories();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Unknown error';
        setShowSuccessMessage(`ການລຶບໝວດໝູ່ລົ້ມເຫລວ: ${errorMessage}`);
      } else {
                  setShowSuccessMessage('ການລຶບໝວດໝູ່ລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.');
      }
      
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => setShowSuccessMessage(null), 5000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-6 sm:px-8 lg:px-10 py-6">
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-md border border-gray-200 p-4 sm:p-6 mb-6 hover:shadow-lg transition-all duration-300">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <div className="flex-1">
            <h1 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              ໝວດໝູ່ບົດຮຽນ
            </h1>
            <p className="mt-2 text-xs sm:text-sm text-gray-600">
              ຈັດການໝວດໝູ່ວິດີໂອເພື່ອຈັດລະບຽບເນື້ອຫາຂອງທ່ານ
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-4 flex-shrink-0">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow text-xs font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              ສ້າງໝວດໝູ່ໃໝ່
            </button>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {categories.map((category) => (
          <div
            key={category.id}
            className="bg-gradient-to-br from-white to-gray-50 overflow-hidden shadow-lg rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div 
                    className="w-6 h-6 rounded-full mr-4 shadow-lg"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 truncate" title={category.name}>
                      {category.name}
                    </h3>
                    <span className="text-sm text-gray-600 font-medium">Order: {category.order}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => openEditModal(category)}
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 hover:shadow-md"
                    title="ແກ້ໄຂໝວດໝູ່"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 hover:shadow-md"
                    title="Delete category"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {category.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2" title={category.description}>
                  {category.description}
                </p>
              )}
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 font-medium">{category.video_count} videos</span>
                <span className="text-xs text-gray-500">
                  {new Date(category.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Success/Error Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`rounded-xl p-4 shadow-lg max-w-sm ${
            showSuccessMessage.includes('successfully') 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800' 
              : 'bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center">
              {showSuccessMessage.includes('successfully') ? (
                <CheckIcon className="h-5 w-5 text-green-400 mr-2" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
              )}
              <p className="text-sm font-medium">{showSuccessMessage}</p>
              <button
                onClick={() => setShowSuccessMessage(null)}
                className="ml-2 text-gray-400 hover:text-gray-600"
                title="Close message"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-gradient-to-br from-white to-gray-50 rounded-3xl text-left overflow-hidden shadow-lg transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="bg-white rounded-xl text-gray-400 hover:text-gray-500 focus:outline-none hover:shadow-md transition-all duration-200"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="bg-gradient-to-br from-white to-gray-50 px-6 pt-6 pb-4 sm:p-8 sm:pb-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h2 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      {editingCategory ? `ແກ້ໄຂໝວດໝູ່: "${editingCategory.name}"` : 'ສ້າງໝວດໝູ່ໃໝ່'}
                    </h2>
                    
                    <div className="mt-4">
                      <form onSubmit={handleSubmit}>
                                                <div className="space-y-6">
                            <div>
                            <label htmlFor="category-name" className="block text-sm font-semibold text-gray-700">
                              ຊື່ໝວດໝູ່
                            </label>
                            <input
                              type="text"
                              id="category-name"
                              value={categoryName}
                              onChange={(e) => setCategoryName(e.target.value)}
                              required
                              className="mt-2 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                              disabled={isSubmitting}
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="category-description" className="block text-sm font-semibold text-gray-700">
                              ຄຳອະທິບາຍ
                            </label>
                            <textarea
                              id="category-description"
                              value={categoryDescription}
                              onChange={(e) => setCategoryDescription(e.target.value)}
                              rows={3}
                              className="mt-2 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                              disabled={isSubmitting}
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="category-color" className="block text-sm font-semibold text-gray-700">
                              ສີ
                            </label>
                            <div className="mt-2 flex items-center space-x-4">
                              <input
                                type="color"
                                id="category-color"
                                value={categoryColor}
                                onChange={(e) => setCategoryColor(e.target.value)}
                                className="h-12 w-20 border border-gray-300 rounded-xl shadow-sm"
                                disabled={isSubmitting}
                              />
                              <span className="text-sm text-gray-600">
                                ເລືອກສີສໍາລັບໝວດໝູ່ນີ້
                              </span>
                            </div>
                          </div>

                          <div>
                            <label htmlFor="category-order" className="block text-sm font-semibold text-gray-700">
                              ລຽງຕຳແຫນ່ງທີ່
                            </label>
                            <input
                              type="number"
                              id="category-order"
                              value={categoryOrder}
                              onChange={(e) => setCategoryOrder(Number(e.target.value))}
                              className="mt-2 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 sm:px-8 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-lg px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-base font-medium text-white hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ກຳລັງບັນທຶກ...
                    </>
                  ) : (
                    editingCategory ? 'ບັນທຶກ' : 'ສ້າງໝວດໝູ່ໃໝ່'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={isSubmitting}
                  className="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-lg px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && categoryToDelete && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-gradient-to-br from-white to-gray-50 rounded-3xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-gradient-to-br from-white to-gray-50 px-6 pt-6 pb-4 sm:p-8 sm:pb-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-red-100 to-rose-100 sm:mx-0 sm:h-10 sm:w-10 shadow-lg">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-xl leading-6 font-bold text-gray-900">
                      ລຶບໝວດໝູ່ນີ້
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        ທ່ານແນ່ໃຈບໍ່ວ່າທ່ານຕ້ອງການລຶບໝວດໝູ່ນີ້ຫຼືບໍ່?
                      </p>
                      {categoryToDelete.video_count > 0 && (
                        <p className="text-sm text-red-600 mt-2">
                          ⚠️ ນີ້ໝວດໝູ່ນີ້ໄດ້ໃຊ້ໃນ {categoryToDelete.video_count} ວິດີໂອ. 
                          ທ່ານບໍ່ສາມາດລຶບໝວດໝູ່ນີ້ໄດ້ຖືກຈັດສັດໃຫ້ກັບວິດີໂອຫຼືບໍ່?
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDeleteCategory}
                  disabled={categoryToDelete.video_count > 0}
                  className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-lg px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-base font-medium text-white hover:from-red-700 hover:to-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  ລຶບໝວດໝູ່ນີ້
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setCategoryToDelete(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-lg px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-all duration-200"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories; 